import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertService, DialogService } from '@gains-com/ui-angular';
import { firstValueFrom, of, timeout } from 'rxjs';
import { getDialogResult } from '../utils/dialog.js';
import { MERCH_MODE_GUARD_TIMEOUT_MS } from '../auth/merch-mode.guard.js';
import {
  RedemptionDialogData,
  RedemptionsDialogComponent,
} from '../components/payments/redemptions/redemptions-dialog.component.js';
import {
  PurchasesDialogComponent,
  PurchasesDialogData,
} from '../components/payments/purchases/purchases-dialog.component.js';
import {
  WalletCloseResult,
  WalletDialog,
  WalletDialogData,
  WalletView,
} from '../components/wallet/wallet-dialog.component.js';
import { LOGGER_FACTORY } from './logger/logger-factory.provider.js';
import { IntercomClient } from './intercom-client.service.js';
import { MerchDialogService } from './merch-dialog.service.js';
import { ActivePurchaseViewService } from './view-states/active-purchase-view.service.js';
import { ActiveRedemptionViewService } from './view-states/active-redemption-view.service.js';
import { LocationGateViewService } from './view-states/location-gate-view.service.js';
import { DeviceModeViewService } from './view-states/device-mode-view.service.js';
import { MerchViewService } from './view-states/merch-view.service.js';
import { PURCHASE_LIMIT_REACHED_MESSAGE } from '../components/payments/common.js';
import { UserViewService } from './view-states/user-view.service.js';
import {
  TimeoutDialogComponent,
  TimeoutDialogData,
} from '../components/timeout/timeout-dialog.component.js';
import { AuthService } from './auth.service.js';
import { RedemptionsService } from './redemption.service.js';
import { AuthComponent, AuthDialogData } from '../auth/auth.component.js';
import { DialogKey } from '../shared/dialog-keys.js';
import { CREDIT_CARD_ENTITY_TYPES, DocumentVerificationTypeId } from '@gains-com/shared';
import {
  KycDialogComponent,
  KycDialogData,
  KycDialogResult,
} from '../components/kyc-dialog/index.js';
import {
  DocumentVerificationDialogComponent,
  DocumentVerificationDialogData,
  DocumentVerificationDialogResult,
} from '../components/document-verification-dialog/index.js';

@Injectable({
  providedIn: 'root',
})
export class WalletDialogService {
  private readonly _userView = inject(UserViewService);
  private readonly _dialog = inject(DialogService);
  private readonly _router = inject(Router);
  private readonly _intercom = inject(IntercomClient);
  private readonly _auth = inject(AuthService);
  private readonly _redemptions = inject(RedemptionsService);
  private readonly _merchDialog = inject(MerchDialogService);
  private readonly _activePurchaseViewService = inject(
    ActivePurchaseViewService
  );
  private readonly _activeRedemptionViewService = inject(
    ActiveRedemptionViewService
  );
  private readonly _locationGate = inject(LocationGateViewService);
  private readonly _deviceModeView = inject(DeviceModeViewService);
  private readonly _merchView = inject(MerchViewService);
  private readonly _alert = inject(AlertService);
  private readonly _logger = inject(LOGGER_FACTORY).create('WalletDialogService');

  private _walletDialogRef: any = null;
  /**
   * Captures the most recent `initial_view` so the back-to-wallet recursion
   * (Buy Coins → both-disabled state → back) can re-open the wallet on the
   * same view the user originally entered (e.g. `top_up`).
   */
  private _lastWalletInitialView: WalletView | undefined;

  async openPurchaseModal({
    source = 'direct',
  }: { source?: 'wallet' | 'direct' } = {}) {
    // A logged-out visitor has no user-view state, which the purchases dialog
    // reads as "every per-account payment flag is off" and renders as
    // "payments disabled on your account". Any anonymous entry — e.g. a hero
    // card whose `action_requires_auth` flag is off — must land on the auth
    // dialog instead, matching AuthClickDirective's logged-out branch.
    if (!(await this._auth.userP)) {
      this._dialog.open<AuthComponent, AuthDialogData>(AuthComponent, {
        id: DialogKey.Auth,
        data: { initialView: 'login' },
        disableClose: true,
      });
      return;
    }

    // Merch devices have no gold coins to buy, so every Buy Coins entry lands
    // on the shop instead. Fails open to Buy Coins when the experience never
    // resolves: the server rejects the mint for merch devices regardless, and
    // the sweeps majority must not stall behind a slow `/self`.
    const merchExperience = await firstValueFrom(
      this._merchView.merchExperience$.pipe(
        timeout({ first: MERCH_MODE_GUARD_TIMEOUT_MS, with: () => of(false) })
      ),
      { defaultValue: false }
    ).catch(() => false);
    if (merchExperience) {
      void this._merchDialog.openShop();
      return;
    }

    if (!this._timeoutCheckPassed()) {
      return;
    }

    if (this._deviceModeView.purchaseCapReached()) {
      this._alert.notifyWarning('Purchase Limit Reached', PURCHASE_LIMIT_REACHED_MESSAGE);
      return;
    }

    if (!(await this._locationCheckPassed('purchase'))) {
      return;
    }

    const ref = this._dialog.open<PurchasesDialogComponent, PurchasesDialogData>(
      PurchasesDialogComponent,
      {
        // Keyed so CrmPopupNotificationService can hold its queue while a
        // purchase is in flight.
        id: DialogKey.Purchases,
        data: { source },
        // BuyCoinsComponent manages its own focus via afterRenderEffect so it
        // can correctly target the amount input even when isLoading delays
        // the input's render past dialog-open time. MatDialog's default
        // autoFocus picks the close button in that case.
        autoFocus: false,
      }
    );

    const result = await getDialogResult(ref);

    this._activePurchaseViewService.clearActivePurchasePayment();

    if (result === 'reopen-wallet') {
      // The outer openWalletModal() call (if any) is still awaiting us, so its
      // finally block hasn't cleared _walletDialogRef yet. Clear it manually
      // so openWalletModal()'s in-flight guard doesn't early-return.
      this._walletDialogRef = null;
      await this.openWalletModal({ initial_view: this._lastWalletInitialView });
      return;
    }

    if (result === 'contact-support') {
      this.openCustomerSupport();
    }

    return result;
  }

  openCustomerSupport() {
    this._intercom.open();
  }

  /**
   * Opens the KYC verification dialog.
   * @param data Optional dialog data
   */
  async openKycDialog(data?: KycDialogData): Promise<KycDialogResult> {
    const ref = this._dialog.open<KycDialogComponent, KycDialogData>(
      KycDialogComponent,
      {
        id: DialogKey.KycVerification,
        data,
        disableClose: true,
      }
    );

    return ((await getDialogResult(ref)) as KycDialogResult) ?? 'cancelled';
  }

  /**
   * Opens the document verification dialog.
   * @param data Dialog data including target entity configuration
   */
  async openDocumentVerificationDialog(
    data?: DocumentVerificationDialogData
  ): Promise<DocumentVerificationDialogResult> {
    const ref = this._dialog.open<
      DocumentVerificationDialogComponent,
      DocumentVerificationDialogData
    >(DocumentVerificationDialogComponent, {
      id: DialogKey.DocumentVerification,
      data,
      disableClose: true,
    });

    return (
      ((await getDialogResult(ref)) as DocumentVerificationDialogResult) ??
      'cancelled'
    );
  }

  async openRedeemModal() {
    const ref = this._dialog.open<
      RedemptionsDialogComponent,
      RedemptionDialogData
    >(RedemptionsDialogComponent, {
      data: {},
    });

    const result = await getDialogResult<
      'contact-support' | 'additional-verification' | null
    >(ref);

    this._activeRedemptionViewService.clearActiveRedemptionPayment();

    if (result === 'contact-support') {
      this.openCustomerSupport();
    } else if (result === 'additional-verification') {
      await this.openDocumentVerificationDialog({
        targetEntity: {
          documentVerificationTypeId:
            DocumentVerificationTypeId.OwnerOfPaymentMethod,
          targetEntityTypeId: CREDIT_CARD_ENTITY_TYPES as string[],
        },
      });
    }
  }

  /**
   * Starts the redemption flow with KYC and document verification checks.
   * Opens separate KYC and document verification dialogs sequentially if needed,
   * then proceeds to redemption dialog.
   * Can be called directly without opening the wallet dialog first.
   */
  async startRedemptionFlow() {
    // The server evaluates the same account limits and document verification the
    // `payment_method_verification` transaction check does, so its verdict — not the
    // `/self` purchase flags below — decides whether we prompt. Those flags are a
    // different computation and have silently disagreed in prod, skipping the prompt and
    // leaving the redemption blocked with no way out. It rides along with the other
    // pre-flight reads so it costs no extra round-trip on the way to the confirm dialog,
    // and is never cached — a verdict can flip mid-session the moment the user's
    // verification is approved.
    const [kycVerified, hasHadCardPurchase, requirements] = await Promise.all([
      this._auth.kycVerified,
      this._userView.syncHasHadCardPurchase(),
      this._redemptions.getRedemptionRequirements().catch((err: unknown): null => {
        this._logger.warn(
          'startRedemptionFlow: redemption requirements lookup failed, falling back to /self flags',
          { error: err instanceof Error ? err.message : String(err) }
        );
        return null;
      }),
      this._userView.syncUserFeatures(),
    ]);

    const serverVerdict = requirements?.document_verification_required ?? null;

    let legacyDocumentVerificationNeeded = false;
    let paymentMethodVerified: boolean | undefined;

    if (hasHadCardPurchase) {
      paymentMethodVerified = await this._auth.fiatPaymentMethodVerifiedRefreshed;
      legacyDocumentVerificationNeeded = !paymentMethodVerified;
    }

    const documentVerificationNeeded =
      serverVerdict ?? legacyDocumentVerificationNeeded;

    // Both signals are logged so the remaining `/self` read anomaly stays measurable:
    // `server_verdict=true` with `hasHadCardPurchase=false` is an incident this prevented.
    this._logger.info('startRedemptionFlow: verification decision', {
      kycVerified,
      hasHadCardPurchase,
      paymentMethodVerified,
      serverVerdict,
      legacyDocumentVerificationNeeded,
      documentVerificationNeeded,
    });

    if (!kycVerified) {
      const kycResult = await this.openKycDialog({
        autoClose: documentVerificationNeeded,
      });

      this._logger.info('startRedemptionFlow: KYC dialog result', { kycResult });

      if (kycResult !== 'verified') {
        return;
      }
    }

    if (documentVerificationNeeded) {
      const docResult = await this.openDocumentVerificationDialog({
        targetEntity: {
          documentVerificationTypeId:
            DocumentVerificationTypeId.OwnerOfPaymentMethod,
          targetEntityTypeId: CREDIT_CARD_ENTITY_TYPES as string[],
        },
      });

      this._logger.info('startRedemptionFlow: doc verification result', { docResult });

      if (docResult !== 'verified' && docResult !== 'blocked') {
        return;
      }
    }

    await this.openRedeemModal();
  }

  async openWalletModal({ initial_view }: { initial_view?: WalletView } = {}) {
    if (this._walletDialogRef) {
      return;
    }

    this._lastWalletInitialView = initial_view;

    this._walletDialogRef = this._dialog.open<WalletDialog, WalletDialogData>(
      WalletDialog,
      {
        data: {
          initialView: initial_view,
        },
      }
    );

    try {
      const result = await getDialogResult<WalletCloseResult>(
        this._walletDialogRef
      );

      if (result === 'purchase') {
        await this.openPurchaseModal({ source: 'wallet' });
      } else if (result === 'redemption') {
        await this.startRedemptionFlow();
      } else if (result === 'shop') {
        // Not awaited: the shop funnel can loop (list -> options -> back ->
        // list) and the finally below releases the wallet's reopen guard, so
        // holding it for the whole funnel would leave the Wallet pill dead
        // between steps. Matches the timing of the old route navigation.
        void this._merchDialog.openShop();
      } else if (result === 'orders') {
        await this._router.navigateByUrl('/shop/orders');
      } else if (result === 'transactions') {
        await this._router.navigateByUrl('/transactions');
      }
    } finally {
      this._walletDialogRef = null;
    }
  }

  async openDailyClaim() {
    if (!this._timeoutCheckPassed()) {
      return;
    }

    if (!(await this._locationCheckPassed('daily_claim'))) {
      return;
    }

    // Re-checked after the awaited gate: a second entry can resolve its own
    // gate in the same turn and must not stack a second claim dialog.
    if (this._dialog.getDialogRefById(DialogKey.DailyClaim)) {
      return;
    }

    this._dialog.open<WalletDialog, WalletDialogData>(WalletDialog, {
      id: DialogKey.DailyClaim,
      data: { initialView: 'daily_claim' },
    });
  }

  // Redemptions are deliberately NOT gated — startRedemptionFlow never runs
  // this check.
  private _locationCheckPassed(action: 'purchase' | 'daily_claim') {
    return this._locationGate.requireVerifiedForAction(action);
  }

  private _timeoutCheckPassed() {
    const timeout = this._userView.state?.timeout_until;
    const timeoutOngoing = timeout && Date.now() < timeout;

    if (timeoutOngoing) {
      this._dialog.open<TimeoutDialogData, TimeoutDialogData>(
        TimeoutDialogComponent,
        {
          data: { timeout_until: new Date(timeout) },
        }
      );
      return false;
    }

    return true;
  }
}
